import { add, differenceInCalendarDays, format, subDays } from 'date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { DateRange, RangeFocus, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface JumpToDateRangeControlProps {
    startDate: string;
    endDate: string;
    onChangeDate: (data: { start_date: string; end_date: string }) => void;
}

const MIN_DAYS = 16;
const MAX_DAYS = 60;

const parseOrDefault = (value: string, fallback: Date) => {
    if (!value) return fallback;
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? fallback : parsed;
};

/**
 * Versi desktop dari "Jump to Date": popover inline (bukan Modal), tanpa tombol
 * submit, dan rentang di luar 16-60 hari di-klem otomatis (bukan ditolak lewat
 * alert validasi) — lihat plan/known-issues untuk alasan. Versi mobile tetap
 * memakai JumpToDateDialogV2 (Modal + tombol Jump + alert), tidak terpengaruh.
 */
const JumpToDateRangeControl: React.FC<JumpToDateRangeControlProps> = ({ startDate, endDate, onChangeDate }) => {
    const defaultStart = new Date();
    const defaultEnd = add(defaultStart, { days: 30 });

    const [isOpen, setIsOpen] = useState(false);
    const [dateRange, setDateRange] = useState(() => ({
        startDate: parseOrDefault(startDate, defaultStart),
        endDate: parseOrDefault(endDate, defaultEnd),
    }));

    const containerRef = useRef<HTMLDivElement>(null);
    const latestRangeRef = useRef(dateRange);
    const hasFocusedEndRef = useRef(false);

    // Sinkronkan tampilan dengan rentang aktif dari parent (mis. setelah navigasi bulan)
    useEffect(() => {
        if (!isOpen) {
            setDateRange({
                startDate: parseOrDefault(startDate, defaultStart),
                endDate: parseOrDefault(endDate, defaultEnd),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                hasFocusedEndRef.current = false;
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (!selection.startDate || !selection.endDate) return;
        const next = { startDate: selection.startDate, endDate: selection.endDate };
        latestRangeRef.current = next;
        setDateRange(next);
    };

    const handleRangeFocusChange = (focusedRange: RangeFocus) => {
        const step = focusedRange[1];

        if (step === 1) {
            hasFocusedEndRef.current = true;
            return;
        }

        // step kembali ke 0 SETELAH sebelumnya sempat 1 = kedua tanggal sudah lengkap dipilih
        if (step === 0 && hasFocusedEndRef.current) {
            hasFocusedEndRef.current = false;

            let { startDate: s, endDate: e } = latestRangeRef.current;
            const dayCount = differenceInCalendarDays(e, s) + 1;

            if (dayCount > MAX_DAYS) {
                s = subDays(e, MAX_DAYS - 1);
            } else if (dayCount < MIN_DAYS) {
                s = subDays(e, MIN_DAYS - 1);
            }

            const clamped = { startDate: s, endDate: e };
            latestRangeRef.current = clamped;
            setDateRange(clamped);
            setIsOpen(false);

            onChangeDate({
                start_date: format(s, 'yyyy-MM-dd'),
                end_date: format(e, 'yyyy-MM-dd'),
            });
        }
    };

    return (
        <div className="position-relative" ref={containerRef}>
            <button
                type="button"
                className="btn btn-sm btn-outline-primary p-2 d-flex align-items-center gap-1"
                onClick={() => setIsOpen((prev) => !prev)}
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="16" height="16" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                    <path d="M16 3l0 4"></path>
                    <path d="M8 3l0 4"></path>
                    <path d="M4 11l16 0"></path>
                    <path d="M8 15h2v2h-2z"></path>
                </svg>
                {format(dateRange.startDate, 'dd MMM yyyy')} - {format(dateRange.endDate, 'dd MMM yyyy')}
            </button>

            {isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        zIndex: 1056,
                        top: '100%',
                        left: 0,
                        marginTop: '8px',
                        border: '1px solid #dfe2e5',
                        borderRadius: '8px',
                        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
                    }}
                >
                    <DateRange
                        onChange={handleDateChange}
                        onRangeFocusChange={handleRangeFocusChange}
                        moveRangeOnFirstSelection={false}
                        ranges={[{ ...dateRange, key: 'selection' }]}
                        months={1}
                        direction="vertical"
                        className="shadow-sm"
                    />
                </div>
            )}
        </div>
    );
};

export default JumpToDateRangeControl;
